import { useState, useEffect } from 'react';
import {
  Card,
  Layout,
  Text,
  Button,
  Modal,
  FormLayout,
  TextField,
  Select,
  Badge,
  Box,
  Toast,
  Frame,
  IndexTable,
  Checkbox,
  Divider,
  LegacyStack,
  RangeSlider,
  ColorPicker,
  Tabs,
} from '@shopify/polaris';

interface TeamShowcase {
  id: string;
  name: string;
  settings: Record<string, any>;
  teamMemberIds: string[];
}

interface TeamMember {
  id: string;
  name: string;
  designation: string;
  image_url: string;
}

const defaultSettings = {
  // View & Layout
  view: 'grid',
  layout: 'Card',
  // Grid settings
  columns: 3,
  columnGap: 20,
  containerWidth: 1200,
  // Card styling
  cardBg: '#ffffff',
  cardBorderRadius: 8,
  cardBorder: '1px solid #e0e0e0',
  cardShadow: '0 2px 8px rgba(0,0,0,0.08)',
  cardPadding: 20,
  // Image
  imageHeight: 250,
  imageBorderRadius: 0,
  imageBorder: 'none',
  // Typography
  nameColor: '#1a1a1a',
  nameFontSize: 18,
  nameFontWeight: '600',
  designationColor: '#666666',
  designationFontSize: 14,
  descriptionColor: '#444444',
  descriptionFontSize: 14,
  // Content
  showDesignation: true,
  showDescription: true,
  showSocial: true,
  // Carousel
  slidesToShow: 3,
  slidesToScroll: 1,
  carouselSpeed: 500,
  carouselGap: 20,
  carouselTransition: 'slide',
  carouselInfinite: true,
  carouselAutoplay: false,
  // Marquee
  marqueeDirection: 'left',
  marqueeSpeed: 30,
  marqueeInfinite: true,
  marqueePauseOnHover: true,
  // Hover
  hoverAnimation: 'none',
  // Global
  backgroundColor: '',
  padding: 40,
  borderRadius: 0,
};

const viewOptions = [
  { label: 'Grid', value: 'grid' },
  { label: 'Carousel', value: 'carousel' },
  { label: 'Flex', value: 'flex' },
  { label: 'Marquee', value: 'marquee' },
  { label: 'Table', value: 'table' },
  { label: 'Filterable', value: 'filterable' },
];

const gridLayoutOptions = [
  { label: 'Card', value: 'Card' },
  { label: 'Horizontal Card', value: 'HorizontalCard' },
  { label: 'Overlay Card', value: 'OverlayCard' },
  { label: 'Tiles', value: 'Tiles' },
  { label: 'Spotlight', value: 'Spotlight' },
  { label: 'Corner Frame', value: 'CornerFrame' },
  { label: 'Flip Box', value: 'Flipbox' },
  { label: 'Aura Live', value: 'AuraLive' },
];

const flexLayoutOptions = [
  { label: 'Split Left', value: 'split-left' },
  { label: 'Split Right', value: 'split-right' },
  { label: 'Centered', value: 'centered' },
];

const hoverOptions = [
  { label: 'None', value: 'none' },
  { label: 'Float 3D', value: 'float3d' },
  { label: 'Tilt', value: 'tilt' },
  { label: 'Blur', value: 'blur' },
  { label: 'Slide Top', value: 'slideTop' },
  { label: 'Shadow Pop', value: 'shadowPop' },
  { label: 'Shadow Drop', value: 'shadowDrop' },
];

function TeamShowcases({ onEditDesign }: { onEditDesign?: (id: string) => void }) {
  const [showcases, setShowcases] = useState<TeamShowcase[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingShowcase, setEditingShowcase] = useState<TeamShowcase | null>(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState<Record<string, any>>({ ...defaultSettings });

  const API_URL = 'https://ts-team-shopify.anonnaabir.workers.dev';

  useEffect(() => {
    fetchShowcases();
    fetchTeamMembers();
  }, []);

  const fetchShowcases = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team-showcases`);
      const result = await response.json();
      if (result.success) {
        const mappedShowcases = result.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          settings: item.settings ? JSON.parse(item.settings) : {},
          teamMemberIds: item.team_member_ids ? JSON.parse(item.team_member_ids) : [],
        }));
        setShowcases(mappedShowcases);
      }
    } catch (error) {
      console.error('Error fetching showcases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team-members`);
      const result = await response.json();
      if (result.success) {
        setTeamMembers(result.data);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team-showcases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: showcaseName,
          teamMemberIds: selectedMemberIds,
          settings,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setToastMessage('Showcase created successfully');
        setToastActive(true);
        setShowModal(false);
        fetchShowcases();
        resetForm();
      }
    } catch (error) {
      setToastMessage('Error creating showcase');
      setToastActive(true);
    }
  };

  const handleUpdate = async () => {
    if (!editingShowcase) return;
    try {
      const response = await fetch(`${API_URL}/api/team-showcases/${editingShowcase.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: showcaseName,
          teamMemberIds: selectedMemberIds,
          settings,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setToastMessage('Showcase updated successfully');
        setToastActive(true);
        setShowModal(false);
        fetchShowcases();
        resetForm();
      }
    } catch (error) {
      setToastMessage('Error updating showcase');
      setToastActive(true);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this showcase?')) return;
    try {
      const response = await fetch(`${API_URL}/api/team-showcases/${id}`, { method: 'DELETE' });
      const result = await response.json();
      if (result.success) {
        setToastMessage('Showcase deleted');
        setToastActive(true);
        fetchShowcases();
      }
    } catch (error) {
      setToastMessage('Error deleting showcase');
      setToastActive(true);
    }
  };

  const [showcaseName, setShowcaseName] = useState('');

  const resetForm = () => {
    setShowcaseName('');
    setSelectedMemberIds([]);
    setSettings({ ...defaultSettings });
    setEditingShowcase(null);
    setActiveTab(0);
  };

  const openEditModal = (showcase: TeamShowcase) => {
    setEditingShowcase(showcase);
    setShowcaseName(showcase.name || '');
    setSelectedMemberIds(showcase.teamMemberIds || []);
    setSettings({ ...defaultSettings, ...(showcase.settings || {}) });
    setShowModal(true);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const toggleMember = (memberId: string) => {
    setSelectedMemberIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const updateSetting = (key: string, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const toastMarkup = toastActive ? (
    <Toast content={toastMessage} onDismiss={() => setToastActive(false)} />
  ) : null;

  const tabs = [
    { id: '0', content: 'Content', panel: null },
    { id: '1', content: 'Style', panel: null },
    { id: '2', content: 'Typography', panel: null },
    { id: '3', content: 'Members', panel: null },
  ];

  const rowMarkup = showcases.map((showcase, index) => (
    <IndexTable.Row id={showcase.id} key={showcase.id} position={index}>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">{showcase.name}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm" as="span" color="subdued">{showcase.id}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge>{showcase.settings?.view || 'grid'}</Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge>{showcase.settings?.layout || 'Card'}</Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{showcase.teamMemberIds?.length || 0}</IndexTable.Cell>
      <IndexTable.Cell>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button size="slim" onClick={() => openEditModal(showcase)}>Edit</Button>
          {onEditDesign && (
            <Button size="slim" onClick={() => onEditDesign(showcase.id)}>Edit Design</Button>
          )}
          <Button size="slim" variant="primary" onClick={() => handleDelete(showcase.id)}>Delete</Button>
        </div>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Frame>
      <Layout>
        <Layout.Section>
          <Card>
            <Box padding="400">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text variant="headingMd" as="h2">Team Showcases</Text>
                <Button variant="primary" onClick={openCreateModal}>Create Showcase</Button>
              </div>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <IndexTable
              resourceName={{ singular: 'showcase', plural: 'showcases' }}
              itemCount={showcases.length}
              headings={[
                { title: 'Name' },
                { title: 'ID' },
                { title: 'View' },
                { title: 'Layout' },
                { title: 'Members' },
                { title: 'Actions' },
              ]}
              selectable={false}
              loading={loading}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingShowcase ? 'Edit Team Showcase' : 'Create Team Showcase'}
        primaryAction={{
          content: editingShowcase ? 'Update' : 'Create',
          onAction: editingShowcase ? handleUpdate : handleCreate,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => { setShowModal(false); resetForm(); } }]}
        large
      >
        <Modal.Section>
          <Tabs tabs={tabs} selected={activeTab} onSelect={setActiveTab}>
            {/* Content Tab */}
            {activeTab === 0 && (
              <FormLayout>
                <TextField
                  label="Showcase Name"
                  value={showcaseName}
                  onChange={setShowcaseName}
                  autoComplete="off"
                />
                <Select
                  label="View Style"
                  options={viewOptions}
                  value={settings.view}
                  onChange={(v) => updateSetting('view', v)}
                />
                {(settings.view === 'grid' || settings.view === 'filterable') && (
                  <Select
                    label="Card Layout"
                    options={gridLayoutOptions}
                    value={settings.layout}
                    onChange={(v) => updateSetting('layout', v)}
                  />
                )}
                {settings.view === 'flex' && (
                  <Select
                    label="Flex Layout"
                    options={flexLayoutOptions}
                    value={settings.layout}
                    onChange={(v) => updateSetting('layout', v)}
                  />
                )}
                {settings.view === 'grid' && (
                  <>
                    <TextField
                      label="Columns"
                      value={String(settings.columns)}
                      onChange={(v) => updateSetting('columns', parseInt(v) || 3)}
                      type="number"
                      autoComplete="off"
                    />
                    <TextField
                      label="Column Gap (px)"
                      value={String(settings.columnGap)}
                      onChange={(v) => updateSetting('columnGap', parseInt(v) || 20)}
                      type="number"
                      autoComplete="off"
                    />
                  </>
                )}
                <TextField
                  label="Container Width (px)"
                  value={String(settings.containerWidth)}
                  onChange={(v) => updateSetting('containerWidth', parseInt(v) || 1200)}
                  type="number"
                  autoComplete="off"
                />
                {settings.view === 'carousel' && (
                  <>
                    <TextField label="Slides to Show" value={String(settings.slidesToShow)} onChange={(v) => updateSetting('slidesToShow', parseInt(v) || 3)} type="number" autoComplete="off" />
                    <TextField label="Slides to Scroll" value={String(settings.slidesToScroll)} onChange={(v) => updateSetting('slidesToScroll', parseInt(v) || 1)} type="number" autoComplete="off" />
                    <TextField label="Speed (ms)" value={String(settings.carouselSpeed)} onChange={(v) => updateSetting('carouselSpeed', parseInt(v) || 500)} type="number" autoComplete="off" />
                    <Checkbox label="Infinite" checked={settings.carouselInfinite} onChange={(v) => updateSetting('carouselInfinite', v)} />
                    <Checkbox label="Autoplay" checked={settings.carouselAutoplay} onChange={(v) => updateSetting('carouselAutoplay', v)} />
                  </>
                )}
                {settings.view === 'marquee' && (
                  <>
                    <Select label="Direction" options={[{ label: 'Left', value: 'left' }, { label: 'Right', value: 'right' }]} value={settings.marqueeDirection} onChange={(v) => updateSetting('marqueeDirection', v)} />
                    <TextField label="Speed" value={String(settings.marqueeSpeed)} onChange={(v) => updateSetting('marqueeSpeed', parseInt(v) || 30)} type="number" autoComplete="off" />
                    <Checkbox label="Infinite" checked={settings.marqueeInfinite} onChange={(v) => updateSetting('marqueeInfinite', v)} />
                    <Checkbox label="Pause on Hover" checked={settings.marqueePauseOnHover} onChange={(v) => updateSetting('marqueePauseOnHover', v)} />
                  </>
                )}
                <Select
                  label="Hover Animation"
                  options={hoverOptions}
                  value={settings.hoverAnimation}
                  onChange={(v) => updateSetting('hoverAnimation', v)}
                />
              </FormLayout>
            )}

            {/* Style Tab */}
            {activeTab === 1 && (
              <FormLayout>
                <Text variant="headingSm" as="h3">Card</Text>
                <TextField label="Background Color" value={settings.cardBg} onChange={(v) => updateSetting('cardBg', v)} autoComplete="off" />
                <TextField label="Border Radius (px)" value={String(settings.cardBorderRadius)} onChange={(v) => updateSetting('cardBorderRadius', parseInt(v) || 0)} type="number" autoComplete="off" />
                <TextField label="Border" value={settings.cardBorder} onChange={(v) => updateSetting('cardBorder', v)} autoComplete="off" />
                <TextField label="Box Shadow" value={settings.cardShadow} onChange={(v) => updateSetting('cardShadow', v)} autoComplete="off" />
                <TextField label="Padding (px)" value={String(settings.cardPadding)} onChange={(v) => updateSetting('cardPadding', parseInt(v) || 20)} type="number" autoComplete="off" />
                <Divider />
                <Text variant="headingSm" as="h3">Image</Text>
                <TextField label="Height (px)" value={String(settings.imageHeight)} onChange={(v) => updateSetting('imageHeight', parseInt(v) || 250)} type="number" autoComplete="off" />
                <TextField label="Border Radius (px)" value={String(settings.imageBorderRadius)} onChange={(v) => updateSetting('imageBorderRadius', parseInt(v) || 0)} type="number" autoComplete="off" />
                <Divider />
                <Text variant="headingSm" as="h3">Section</Text>
                <TextField label="Background Color (leave empty for none)" value={settings.backgroundColor} onChange={(v) => updateSetting('backgroundColor', v)} autoComplete="off" />
                <TextField label="Padding (px)" value={String(settings.padding)} onChange={(v) => updateSetting('padding', parseInt(v) || 40)} type="number" autoComplete="off" />
              </FormLayout>
            )}

            {/* Typography Tab */}
            {activeTab === 2 && (
              <FormLayout>
                <Text variant="headingSm" as="h3">Name</Text>
                <TextField label="Color" value={settings.nameColor} onChange={(v) => updateSetting('nameColor', v)} autoComplete="off" />
                <TextField label="Font Size (px)" value={String(settings.nameFontSize)} onChange={(v) => updateSetting('nameFontSize', parseInt(v) || 18)} type="number" autoComplete="off" />
                <Select label="Font Weight" options={[{ label: 'Regular', value: '400' }, { label: 'Medium', value: '500' }, { label: 'Semi Bold', value: '600' }, { label: 'Bold', value: '700' }]} value={settings.nameFontWeight} onChange={(v) => updateSetting('nameFontWeight', v)} />
                <Divider />
                <Text variant="headingSm" as="h3">Designation</Text>
                <TextField label="Color" value={settings.designationColor} onChange={(v) => updateSetting('designationColor', v)} autoComplete="off" />
                <TextField label="Font Size (px)" value={String(settings.designationFontSize)} onChange={(v) => updateSetting('designationFontSize', parseInt(v) || 14)} type="number" autoComplete="off" />
                <Divider />
                <Text variant="headingSm" as="h3">Description</Text>
                <TextField label="Color" value={settings.descriptionColor} onChange={(v) => updateSetting('descriptionColor', v)} autoComplete="off" />
                <TextField label="Font Size (px)" value={String(settings.descriptionFontSize)} onChange={(v) => updateSetting('descriptionFontSize', parseInt(v) || 14)} type="number" autoComplete="off" />
                <Divider />
                <Text variant="headingSm" as="h3">Content</Text>
                <Checkbox label="Show Designation" checked={settings.showDesignation} onChange={(v) => updateSetting('showDesignation', v)} />
                <Checkbox label="Show Description" checked={settings.showDescription} onChange={(v) => updateSetting('showDescription', v)} />
              </FormLayout>
            )}

            {/* Members Tab */}
            {activeTab === 3 && (
              <FormLayout>
                <Text variant="headingSm" as="h3">
                  Select Team Members ({selectedMemberIds.length} selected)
                </Text>
                {teamMembers.length === 0 ? (
                  <Text variant="bodySm" as="p" color="subdued">
                    No team members found. Create team members first.
                  </Text>
                ) : (
                  <Box paddingBlockStart="200">
                    <LegacyStack vertical spacing="loose">
                      {teamMembers.map((member) => (
                        <LegacyStack.Item key={member.id}>
                          <Checkbox
                            label={
                              <LegacyStack spacing="tight" alignment="center">
                                {member.image_url && (
                                  <img
                                    src={member.image_url}
                                    alt=""
                                    width="32"
                                    height="32"
                                    style={{ borderRadius: '50%', objectFit: 'cover' }}
                                  />
                                )}
                                <span>
                                  <strong>{member.name}</strong>
                                  {member.designation && (
                                    <span style={{ color: '#666', marginLeft: '8px' }}>
                                      {member.designation}
                                    </span>
                                  )}
                                </span>
                              </LegacyStack>
                            }
                            checked={selectedMemberIds.includes(member.id)}
                            onChange={() => toggleMember(member.id)}
                          />
                        </LegacyStack.Item>
                      ))}
                    </LegacyStack>
                  </Box>
                )}
              </FormLayout>
            )}
          </Tabs>
        </Modal.Section>
      </Modal>

      {toastMarkup}
    </Frame>
  );
}

export default TeamShowcases;
